import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, Clock, SendHorizonal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return toast.error("Please fill in your name, email and message.");
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll reply within one business day.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36 bg-[#050A14]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <SectionLabel testid="contact-label">Say hello</SectionLabel>
            <Reveal>
              <h2 className="font-display font-bold tracking-tight text-white text-4xl md:text-5xl leading-[1.05]">
                Prefer email? <span className="text-[#1E6BFF]">We answer every message ourselves.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-gray-400 text-lg leading-relaxed max-w-md">
                No ticket systems, no bots, no "your enquiry is important to us." Tell us what's
                going on with your business and a senior member of the team will reply personally.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 space-y-4">
                <a
                  href="mailto:info@titanwebsolutions.net"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-[#1E6BFF]/50 transition-colors duration-200 group"
                >
                  <span className="w-11 h-11 rounded-full bg-[#1E6BFF]/15 border border-[#1E6BFF]/30 flex items-center justify-center shrink-0">
                    <Mail size={18} className="text-[#1E6BFF]" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-wider text-gray-500">Email us directly</span>
                    <span className="block text-white font-medium group-hover:text-[#1E6BFF] transition-colors duration-200">
                      info@titanwebsolutions.net
                    </span>
                  </span>
                </a>
                <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <span className="w-11 h-11 rounded-full bg-[#1E6BFF]/15 border border-[#1E6BFF]/30 flex items-center justify-center shrink-0">
                    <Clock size={18} className="text-[#1E6BFF]" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-wider text-gray-500">Typical reply time</span>
                    <span className="block text-white font-medium">Under one business day</span>
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 md:p-10">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      data-testid="contact-name"
                      placeholder="Full name *"
                      value={form.name}
                      onChange={update("name")}
                      maxLength={120}
                      className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF]"
                    />
                    <Input
                      data-testid="contact-email"
                      type="email"
                      placeholder="Email *"
                      value={form.email}
                      onChange={update("email")}
                      className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF]"
                    />
                  </div>
                  <Textarea
                    data-testid="contact-message"
                    placeholder="What's going on with your website or online presence? *"
                    value={form.message}
                    onChange={update("message")}
                    maxLength={2000}
                    className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] min-h-[160px]"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] disabled:opacity-60 text-white font-semibold px-8 py-4 text-base transition-colors duration-200 glow-blue"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        <SendHorizonal size={18} /> Send Message
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 text-center" data-testid="contact-privacy-note">
                    Your message goes straight to our inbox — we never share your details.
                  </p>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
